import { AgeGroup, Unit } from "../../../constants/exportsCountries"
import {
  convertQuantityToBaseUnit,
  removeEmptyStringValuesFromObject,
} from "../IndiaLabelPreview/NutritionIndiaHelper"
import {
  MICRO_NUTRIENT_KEYS,
  USA_DV_VALUES,
  USA_NUTRITION_KEYS,
  USALabelJson,
  USAMandatoryNutrient,
  USAMandatoryNutrientData,
  USAMandatoryNutrientObject,
  USANutritionTableValue,
} from "./USATypes"

export function calculateUSANutritionTableValues(
  label_json: USALabelJson,
): USANutritionTableValue {
  const {
    serving_size = "",
    serving_size_unit = "g",
    net_quantity = "",
    net_quantity_unit = "g",
    household_quanity = "",
    mandatory_nutrient,
    age_group,
  } = label_json

  const serving_per_container = calculateAndRoundOffUSAServingPerContainer(
    serving_size,
    serving_size_unit,
    net_quantity,
    net_quantity_unit,
  )

  const serving_size_in_base_unit = convertQuantityToBaseUnit(
    serving_size,
    serving_size_unit,
  )

  const rounded_serving_size = roundOffUSAServingSize(serving_size_in_base_unit)

  const calories = calculateUSACaloriesAndRoundOff(
    mandatory_nutrient?.[0]?.[USA_NUTRITION_KEYS.ENERGY] || "",
    serving_size_in_base_unit,
  )

  const filtered_mandatory_nutrient: USAMandatoryNutrientObject =
    removeEmptyStringValuesFromObject(mandatory_nutrient[0])

  const mandatory_nutrient_calculated_values = getUSAMandatoryNutrientData(
    filtered_mandatory_nutrient,
    serving_size_in_base_unit,
    age_group,
  )

  return {
    serving_per_container,
    serving_size: rounded_serving_size,
    serving_size_unit: serving_size_unit === "ml" ? "mL" : serving_size_unit,
    household_quantity: household_quanity,
    calories,
    mandatory_nutrient: mandatory_nutrient_calculated_values,
  }
}

export function calculateAndRoundOffUSAServingPerContainer(
  serving_size: string | null | undefined,
  serving_size_unit: Unit | null | undefined,
  net_quantity: string | null | undefined,
  net_quantity_unit: Unit | null | undefined,
): number | string {
  if (
    !serving_size ||
    !serving_size_unit ||
    !net_quantity ||
    !net_quantity_unit
  ) {
    return ""
  }

  const serving_size_num = parseFloat(serving_size)
  const net_quantity_num = parseFloat(net_quantity)

  if (isNaN(serving_size_num) || isNaN(net_quantity_num)) {
    return ""
  }

  const serving_size_in_base_unit = convertQuantityToBaseUnit(
    serving_size_num,
    serving_size_unit,
  )
  const net_quantity_in_base_unit = convertQuantityToBaseUnit(
    net_quantity_num,
    net_quantity_unit,
  )

  const number_of_servings =
    net_quantity_in_base_unit / serving_size_in_base_unit

  if (isFinite(number_of_servings) === false) {
    return ""
  }

  return roundOffUSAServingPerContainer(number_of_servings)
}

export function roundOffUSAServingSize(value: number): number | string {
  if (!value || !isFinite(value) || isNaN(value)) return ""

  let rounded: number
  // If quantity < 2 g (or ml), then round to the nearest 0.1 g (or ml).
  if (value < 2) {
    const factor = 1 / 0.1
    rounded = Math.round(value * factor) / factor
  } else if (value < 5) {
    // If quantity is ≥ 2 g (or ml) and < 5 g (or ml), then round to the nearest 0.5 g (or ml).
    const factor = 1 / 0.5
    rounded = Math.round(value * factor) / factor
  } else {
    // If quantity is ≥ 5 g (or ml), then round to the nearest whole number.
    const factor = 1
    rounded = Math.round(value * factor) / factor
  }

  return Math.round(rounded * 10) / 10
}

export function roundOffUSAServingPerContainer(
  serving_per_container: number,
): string | number {
  if (
    !serving_per_container ||
    !isFinite(serving_per_container) ||
    isNaN(serving_per_container)
  )
    return ""

  let rounded: number
  let is_rounded = false

  if (serving_per_container < 2 || serving_per_container > 5) {
    // If number of servings < 2 or > 5, then round to the nearest whole number and use “about.”
    const rounded_whole = Math.round(serving_per_container)
    is_rounded = rounded_whole !== serving_per_container
    rounded = rounded_whole
  } else {
    // If number of servings is ≥ 2 and ≤ 5, then round to the nearest 0.5 and use “about.”
    const rounded_half = Math.round(serving_per_container * 2) / 2
    is_rounded = rounded_half !== serving_per_container
    rounded = rounded_half
  }

  return is_rounded ? `about ${rounded}` : rounded
}

export function calculateUSACaloriesAndRoundOff(
  energy_per_100g_value: number | string,
  serving_size_in_base_unit: number,
): number | string {
  const parsed_energy =
    typeof energy_per_100g_value === "string"
      ? parseFloat(energy_per_100g_value)
      : energy_per_100g_value

  // Validate inputs: empty, null, undefined, NaN, Infinity
  if (
    parsed_energy === undefined ||
    serving_size_in_base_unit === undefined ||
    parsed_energy === null ||
    serving_size_in_base_unit === null ||
    isNaN(parsed_energy) ||
    isNaN(serving_size_in_base_unit) ||
    !isFinite(parsed_energy) ||
    !isFinite(serving_size_in_base_unit)
  ) {
    return ""
  }

  // Compute calories per serve
  const calories_per_serve = (parsed_energy * serving_size_in_base_unit) / 100

  if (!isFinite(calories_per_serve) || isNaN(calories_per_serve)) {
    return ""
  }

  // If calories per serving < 5, then declare as 0 calories.
  if (calories_per_serve < 5) {
    return 0
  }

  // If calories per serving ≥ 5 and ≤ 50, then round to the nearest 5-calorie increment.
  if (calories_per_serve <= 50) {
    return roundToNearest(calories_per_serve, 5)
  }

  // If calories per serving > 50, then round to the nearest 10-calorie increment.
  return roundToNearest(calories_per_serve, 10)
}

export function getUSAMandatoryNutrientData(
  filtered_mandatory_nutrient: USAMandatoryNutrientObject,
  serving_size_in_base_unit: number,
  age_group: AgeGroup,
): USAMandatoryNutrientData {
  const total_fat_per_100g =
    filtered_mandatory_nutrient[USA_NUTRITION_KEYS.TOTAL_FAT]
  const parse_total_fat_per_100g_value =
    typeof total_fat_per_100g === "string"
      ? parseFloat(total_fat_per_100g)
      : total_fat_per_100g
  const show_trans_fat_and_saturated_fat = parse_total_fat_per_100g_value > 0.5

  const nutrient_data: USAMandatoryNutrientData = {
    TOTAL_FAT: {
      key: USA_NUTRITION_KEYS.TOTAL_FAT,
      per_serve_unit: "g",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.TOTAL_FAT],
        USA_NUTRITION_KEYS.TOTAL_FAT,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    CHOLESTEROL: {
      key: USA_NUTRITION_KEYS.CHOLESTEROL,
      per_serve_unit: "mg",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.CHOLESTEROL],
        USA_NUTRITION_KEYS.CHOLESTEROL,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    SODIUM: {
      key: USA_NUTRITION_KEYS.SODIUM,
      per_serve_unit: "mg",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.SODIUM],
        USA_NUTRITION_KEYS.SODIUM,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    TOTAL_CARBOHYDRATE: {
      key: USA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
      per_serve_unit: "g",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE],
        USA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    DIETARY_FIBER: {
      key: USA_NUTRITION_KEYS.DIETARY_FIBRE,
      per_serve_unit: "g",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.DIETARY_FIBRE],
        USA_NUTRITION_KEYS.DIETARY_FIBRE,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    TOTAL_SUGARS: {
      key: USA_NUTRITION_KEYS.TOTAL_SUGARS,
      per_serve_unit: "g",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.TOTAL_SUGARS],
        USA_NUTRITION_KEYS.TOTAL_SUGARS,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    ADDED_SUGARS: {
      key: USA_NUTRITION_KEYS.ADDED_SUGARS,
      per_serve_unit: "g",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.ADDED_SUGARS],
        USA_NUTRITION_KEYS.ADDED_SUGARS,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    PROTEIN: {
      key: USA_NUTRITION_KEYS.PROTEIN,
      per_serve_unit: "g",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.PROTEIN],
        USA_NUTRITION_KEYS.PROTEIN,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    VITAMIN_D: {
      key: USA_NUTRITION_KEYS.VITAMIN_D,
      per_serve_unit: "mcg",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.VITAMIN_D],
        USA_NUTRITION_KEYS.VITAMIN_D,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    CALCIUM: {
      key: USA_NUTRITION_KEYS.CALCIUM,
      per_serve_unit: "mg",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.CALCIUM],
        USA_NUTRITION_KEYS.CALCIUM,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    IRON: {
      key: USA_NUTRITION_KEYS.IRON,
      per_serve_unit: "mg",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.IRON],
        USA_NUTRITION_KEYS.IRON,
        serving_size_in_base_unit,
        age_group,
      ),
    },
    POTASSIUM: {
      key: USA_NUTRITION_KEYS.POTASSIUM,
      per_serve_unit: "mg",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.POTASSIUM],
        USA_NUTRITION_KEYS.POTASSIUM,
        serving_size_in_base_unit,
        age_group,
      ),
    },
  }

  // Conditionally add SATURATED_FAT and TRANS_FAT if total fat > 0.5
  if (show_trans_fat_and_saturated_fat) {
    nutrient_data.SATURATED_FAT = {
      key: USA_NUTRITION_KEYS.SATURATED_FAT,
      per_serve_unit: "g",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.SATURATED_FAT],
        USA_NUTRITION_KEYS.SATURATED_FAT,
        serving_size_in_base_unit,
        age_group,
      ),
    }
    nutrient_data.TRANS_FAT = {
      key: USA_NUTRITION_KEYS.TRANS_FAT,
      per_serve_unit: "g",
      ...calculatedAndRoundOffUSANutrientPerServeAndDVValue(
        filtered_mandatory_nutrient[USA_NUTRITION_KEYS.TRANS_FAT],
        USA_NUTRITION_KEYS.TRANS_FAT,
        serving_size_in_base_unit,
        age_group,
      ),
    }
  }

  return nutrient_data
}

export function calculatedAndRoundOffUSANutrientPerServeAndDVValue(
  per_100g: string,
  nutrient_key: USAMandatoryNutrient,
  serving_size_in_base_unit: number,
  age_group: AgeGroup,
): { per_serve: string | number; daily_value: string | number } {
  const calculated_per_serve = calculateAndRoundOffUSANutrientPerServe(
    per_100g,
    nutrient_key,
    serving_size_in_base_unit,
  )

  const calculated_daily_value = calculateAndRoundOffUSANutrientDailyValue(
    per_100g,
    nutrient_key,
    serving_size_in_base_unit,
    age_group,
  )

  return {
    per_serve: calculated_per_serve,
    daily_value: calculated_daily_value,
  }
}

export function calculateAndRoundOffUSANutrientPerServe(
  per_100g_value: string,
  nutrient_key: USAMandatoryNutrient,
  serving_size_in_base_unit: number,
): number | string {
  const parse_per_100g_value =
    typeof per_100g_value === "string"
      ? parseFloat(per_100g_value)
      : per_100g_value

  if (
    parse_per_100g_value === undefined ||
    serving_size_in_base_unit === undefined ||
    parse_per_100g_value === null ||
    serving_size_in_base_unit === null ||
    isNaN(parse_per_100g_value) ||
    isNaN(serving_size_in_base_unit) ||
    !isFinite(parse_per_100g_value) ||
    !isFinite(serving_size_in_base_unit)
  ) {
    return ""
  }

  const nutrient_per_serve =
    (parse_per_100g_value * serving_size_in_base_unit) / 100

  if (!isFinite(nutrient_per_serve) || isNaN(nutrient_per_serve)) {
    return ""
  }

  if (
    [
      USA_NUTRITION_KEYS.TOTAL_FAT,
      USA_NUTRITION_KEYS.SATURATED_FAT,
      USA_NUTRITION_KEYS.TRANS_FAT,
    ].includes(nutrient_key)
  ) {
    return USAFatNutrientRounding(nutrient_per_serve)
  }

  if (nutrient_key === USA_NUTRITION_KEYS.CHOLESTEROL) {
    if (nutrient_per_serve < 2) return 0
    if (nutrient_per_serve < 5) return "< 5"
    return roundToNearest(nutrient_per_serve, 5)
  }

  if (nutrient_key === USA_NUTRITION_KEYS.SODIUM) {
    if (nutrient_per_serve < 5) return 0
    if (nutrient_per_serve <= 140) return roundToNearest(nutrient_per_serve, 5)
    return roundToNearest(nutrient_per_serve, 10)
  }

  if (nutrient_key === USA_NUTRITION_KEYS.TOTAL_CARBOHYDRATE) {
    if (nutrient_per_serve < 0.5) return 0
    if (nutrient_per_serve < 1) return "< 1"
    return Math.round(nutrient_per_serve)
  }

  if (nutrient_key === USA_NUTRITION_KEYS.DIETARY_FIBRE) {
    if (nutrient_per_serve < 0.5) return 0
    if (nutrient_per_serve < 1) return "< 1"
    return Math.round(nutrient_per_serve)
  }

  if (nutrient_key === USA_NUTRITION_KEYS.TOTAL_SUGARS) {
    if (nutrient_per_serve < 0.5) return 0
    if (nutrient_per_serve < 1) return "< 1"
    return Math.round(nutrient_per_serve)
  }

  if (nutrient_key === USA_NUTRITION_KEYS.ADDED_SUGARS) {
    if (nutrient_per_serve < 0.5) return 0
    if (nutrient_per_serve < 1) return "< 1"
    return Math.round(nutrient_per_serve)
  }

  if (nutrient_key === USA_NUTRITION_KEYS.PROTEIN) {
    if (nutrient_per_serve < 0.5) return 0
    if (nutrient_per_serve < 1) return "< 1"
    return Math.round(nutrient_per_serve)
  }

  if (nutrient_key === USA_NUTRITION_KEYS.VITAMIN_D) {
    return Math.floor(nutrient_per_serve * 10) / 10
  }

  if (nutrient_key === USA_NUTRITION_KEYS.CALCIUM) {
    return roundToNearest(nutrient_per_serve, 10)
  }

  if (nutrient_key === USA_NUTRITION_KEYS.IRON) {
    return Math.round(nutrient_per_serve * 10) / 10
  }

  if (nutrient_key === USA_NUTRITION_KEYS.POTASSIUM) {
    return roundToNearest(nutrient_per_serve, 10)
  }

  return parseFloat(nutrient_per_serve.toFixed(1))
}

export function calculateAndRoundOffUSANutrientDailyValue(
  per_100g: string,
  nutrient_key: USAMandatoryNutrient,
  serving_size_in_base_unit: number,
  age_group: AgeGroup,
): number | string {
  const USA_NO_DV_NUTRIENT_KEYS = getUSANoDVvalueNutrientByAgeGroup(age_group)
  if (USA_NO_DV_NUTRIENT_KEYS.includes(nutrient_key) || !per_100g) {
    return ""
  }

  serving_size_in_base_unit =
    typeof serving_size_in_base_unit === "string"
      ? parseFloat(serving_size_in_base_unit)
      : serving_size_in_base_unit

  const parse_per_100g_value = parseFloat(per_100g)

  const dv_value: number | null =
    USA_DV_VALUES?.[age_group as keyof typeof USA_DV_VALUES]?.[nutrient_key]
      ?.daily_value

  if (!dv_value || isNaN(dv_value)) {
    return ""
  }

  const percentDV =
    parse_per_100g_value * (serving_size_in_base_unit / dv_value)

  if (isNaN(percentDV) || !isFinite(percentDV)) {
    return ""
  }

  // Same rounding off rule for all other nutrient
  if (MICRO_NUTRIENT_KEYS?.includes(nutrient_key)) {
    // If the % Daily Value (DV) is < 2% then round off to 0% DV.
    if (percentDV < 2) {
      return 0
    } else if (percentDV >= 2 && percentDV < 10) {
      // If the % Daily Value (DV) is > 2% to <10%, then round off to nearest 2% DV increment
      return roundToNearest(percentDV, 2)
    } else if (percentDV >= 10 && percentDV <= 50) {
      // If the % Daily Value (DV) is > 10% to < 50%, then round off to nearest 5% DV increment
      return roundToNearest(percentDV, 5)
    } else {
      // If the % Daily Value (DV) is > 50%, then round off to nearest 10% DV increment
      return roundToNearest(percentDV, 10)
    }
  }

  // If the % Daily Value (DV) is ≥ 1% then express to the nearest 1% DV increment.
  return Math.round(percentDV)
}

export function getUSANoDVvalueNutrientByAgeGroup(age_group: AgeGroup) {
  if (age_group === "1-3 years") {
    return [USA_NUTRITION_KEYS.TRANS_FAT, USA_NUTRITION_KEYS.TOTAL_SUGARS]
  }

  return [
    USA_NUTRITION_KEYS.TRANS_FAT,
    USA_NUTRITION_KEYS.TOTAL_SUGARS,
    USA_NUTRITION_KEYS.PROTEIN,
  ]
}

/**  Rounds a number to the nearest increment.
 * Do not use this function for decimal increments such as 0.1 or 0.5.
 * Use it for whole numbers or increments like 5, 10, etc.
 * @param value The number to round.
 * @param increment The increment to round to.
 * @returns The rounded number.
 */
export function roundToNearest(value: number, increment: number): number {
  return Math.round(value / increment) * increment
}

export function USAFatNutrientRounding(value: number): number {
  if (value < 0.5) return 0
  if (value < 5) return Math.floor(value * 2) / 2
  return Math.round(value)
}
